import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ResourceService } from '../resource/resource.service';
import { IResponse } from './response';

import { Yii } from "libraries/interfaces/Yii";
import { ResourceError } from '../resource/ResourceError';
import { IHeader } from '../resource/IHeader';

@Injectable()
export class ApiResourceService {
    constructor(private readonly resourceService: ResourceService) {
    }

    public getApiUrl(...endpoints: Array<string | number>): string {
        return this.resourceService.getUrl('api', ...endpoints);
    }

    public getApiServerUrl(...endpoints: Array<string | number>): string {
        return this.resourceService.getUrl('apiServer', ...endpoints);
    }

    public get<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult[]> {
        return this.resourceService.get<IResponse<TResult>>(url, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public getOne<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        return this.get<TResult>(url, urlParameters, headerParameters).pipe(
            map(mapOne));
    }

    public getBlob(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<Blob> {
        return this.resourceService.getBlob(url, urlParameters, headerParameters);
    }

    public delete<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult[]> {
        return this.resourceService.delete<IResponse<TResult>>(url, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public deleteWithBody<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader): Observable<TResult[]> {
        return this.resourceService.deleteWithBody<TBody, IResponse<TResult>>(url, body, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public deleteOne<TResult>(url: string, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        return this.delete<TResult>(url, urlParameters, headerParameters).pipe(
            map(mapOne));
    }

    public post<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult[]> {
        return this.resourceService.post<TBody, IResponse<TResult>>(url, body, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public postAndGetHtml<TBody>(url: string, body: any, urlParameters?: object, headerParameters?: IHeader)
        : Observable<string> {
        return this.resourceService.postAndGetHtmlResponse<TBody>(url, body, urlParameters, headerParameters);
    }

    public postOne<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult> {
        return this.post<TBody, TResult>(url, body, urlParameters, headerParameters).pipe(
            map(mapOne));
    }

    public put<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult[]> {
        return this.resourceService.put<TBody, IResponse<TResult>>(url, body, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public putOne<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        return this.put<TBody, TResult>(url, body, urlParameters, headerParameters).pipe(
            map(mapOne));
    }

    public patch<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader)
        : Observable<TResult[]> {
        return this.resourceService.patch<TBody, IResponse<TResult>>(url, body, urlParameters, headerParameters).pipe(
            map(mapApiResponse));
    }

    public patchOne<TBody, TResult>(url: string, body: TBody, urlParameters?: object, headerParameters?: IHeader): Observable<TResult> {
        return this.patch<TBody, TResult>(url, body, urlParameters, headerParameters).pipe(
            map(mapOne));
    }
}

function mapApiResponse<TResult>(response: IResponse<TResult>): TResult[] {
    if (response.error) {
        throw new ResourceError(
            response.code,
            response.message,
            response.data ? response.data : null
        );
    }

    if (response.data === undefined || response.data === null) {
        return [];
    }

    if (!Array.isArray(response.data)) {

        if (response.warning) {
            response.data['warningMessage'] = response.message;
        }

        return [ response.data ];
    }

    return response.data;
}

function mapOne<TResult>(list: TResult[]): TResult {
    if (!list || !list.length) {
        return null;
    }

    if (list.length > 1) {
        throw new Error(Yii.t('wui-shared-js', "La réponse a renvoyé plus qu'un seul élément"));
    }

    return list[0];
}
